import { isEasyAccessOperator } from "app/utils/constants/roleMenuConfig";

/**
 * Builds sticky header context: visibility flags and derived state.
 * Mirrors the logic from AppLayout StickyMenu. No behavior change.
 * @param {Object} params
 * @param {Object} params.finalCustomer - resolved customer (auth or localStorage fallback)
 * @param {number} params.finalType - user type
 * @param {boolean} params.isLoggedIn
 * @returns {Object} context with showCalculator, showReports, isServicePartnerCustomer, etc.
 */
export function buildHeaderContext({ finalCustomer = null, finalType, isLoggedIn }) {
  const customer = finalCustomer || {};
  const dsokSettings = customer?.dsokSettings;
  const customerUISettings = dsokSettings?.customer?.customerUISettings || {};

  const easyAccessOperator = isEasyAccessOperator(finalType);
  const isServicePartnerCustomer =
    customer?.customerType === 7 || customer?.customer_type === 7;
  const superadmin =
    customer?.customerType === 1 || customer?.customerType === 2;
  const showCalculator =
    customer?.customerType === 2 ||
    customer?.customerType === 7 ||
    customer?.customer_type === 7 ||
    (superadmin && finalType === 1);
  const showCalculations = customer?.customerId === 1;
  const showLogs = isLoggedIn && !!customer?.dsokType;

  const enableSettings =
    customer?.dsokSettings?.customer?.customerUISettings?.enableSettings;
  const showSettings =
    isLoggedIn && !!customer?.dsokType && enableSettings;

  const enableReports = customerUISettings.enableReports;
  const enableDispensingOrders =
    customerUISettings.enableDispensingOrders ?? false;
  const canDisplayReports = customer?.dsokType === "gastrokiosk";
  const showReports =
    isLoggedIn &&
    !!customer?.dsokType &&
    (enableReports || canDisplayReports);
  const showDispensingOrders =
    isLoggedIn && !!customer?.dsokType && !!enableDispensingOrders;

  let dispensingOrdersUrl = null;
  if (showDispensingOrders && dsokSettings?.customer?.locations?.[1]) {
    const c = dsokSettings.customer;
    const beDomain = c?.beDomain ?? "";
    let env = "prod";
    if (beDomain.includes("staging")) env = "stage";
    if (typeof window !== "undefined" && window.location?.origin?.includes("localhost")) {
      env = "dev";
    }
    const url = `${c.code}/${c.locations[1].code}/${env}/${c.customerId}`;
    dispensingOrdersUrl = `/customer/dsok/dispensing-orders/${url}`;
  }

  const currentRelation = resolveRelation(customer) || {};
  const relationshipType = currentRelation?.relationshipType ?? null;
  const customerTypeRelation = currentRelation?.customerType ?? null;
  const parentCustomerIdRel = currentRelation?.parentCustomerId ?? null;
  // A child inherits relationshipType from its parent, but that inherited value
  // may not be persisted on the child. Treat a child that references a parent as
  // eligible so the "Switch company" action still shows for children.
  const isMultiLicense =
    isLoggedIn &&
    (relationshipType === "multiLicense" ||
      (customerTypeRelation === "child" && !!parentCustomerIdRel));

  return {
    easyAccessOperator,
    isServicePartnerCustomer,
    superadmin,
    showCalculator,
    showCalculations,
    showLogs,
    showSettings,
    showReports,
    showDispensingOrders,
    dispensingOrdersUrl,
    isMultiLicense,
    isLoggedIn,
    dsokType: customer?.dsokType,
    finalCustomer: customer,
    finalType,
  };
}

function safeParse(maybeJson) {
  if (!maybeJson) return null;
  if (typeof maybeJson === "object") return maybeJson;
  if (typeof maybeJson !== "string") return null;
  try {
    return JSON.parse(maybeJson);
  } catch (_e) {
    return null;
  }
}

function resolveRelation(c) {
  const parsed = safeParse(c?.dsokSettings);
  return parsed?.customer?.customerRelation ?? c?.customerRelation ?? null;
}
